/**
 * Evaluation Feedback Panel
 *
 * Called directly from evaluateMessage.js.
 * Exports showEvaluationFeedback(type, messageId, anchorEl, callbacks) — the panel
 * initialises lazily on the first call so no separate setup step is required.
 *
 *  Like    → "Share your feedback" with neutral feedback topics
 *  Dislike → "What could be better?" with negative reason chips
 *
 * Chip click → selects/highlights a reason (does NOT auto-submit).
 * Textarea   → always visible for an optional comment.
 * Submit     → fires callbacks.onSubmit(feedbackText)
 * Regenerate → fires callbacks.onRegenerate(feedbackText)
 *
 * @callback callbacks.onSubmit     - called with combined feedbackText on Submit
 * @callback callbacks.onRegenerate - called with combined feedbackText on Regenerate
 * @callback callbacks.onCancel     - called when panel dismissed without action
 */

const PANEL_ID = 'gc-eval-feedback-panel';

const CONFIG = {
    like: {
        heading: 'Share your feedback',
        iconCls: 'gc-ef-icon--like',
        icon: '<i class="fa-regular fa-comment-dots" aria-hidden="true"></i>',
        reasons: [
            { label: 'Character consistency', icon: '🎭' },
            { label: 'Creativity', icon: '✨' },
            { label: 'Detail level', icon: '📖' },
            { label: 'Tone', icon: '🎵' },
            { label: 'Relevance', icon: '🎯' },
        ],
    },
    dislike: {
        heading: 'What could be better?',
        iconCls: 'gc-ef-icon--dislike',
        icon: '<i class="fa-regular fa-thumbs-down"></i>',
        reasons: [
            { label: 'Too short', icon: '📏' },
            { label: 'Out of character', icon: '🎭' },
            { label: 'Wrong tone', icon: '🎵' },
            { label: 'Repetitive', icon: '🔁' },
            { label: 'Too long', icon: '📜' },
        ],
    },
};

let _panel = null;
let _callbacks = null;
let _type = null;
let _anchorEl = null;
let _closeTimer = null;
let _submitted = false;
let _saving = false;

function initPanel() {
    if (document.getElementById(PANEL_ID)) {
        _panel = document.getElementById(PANEL_ID);
        return;
    }

    document.body.insertAdjacentHTML('beforeend', `
        <div id="${PANEL_ID}" class="gc-ef-panel" role="dialog" aria-label="Feedback">
            <div class="gc-ef-arrow"></div>
            <div class="gc-ef-header">
                <span class="gc-ef-icon"></span>
                <p class="gc-ef-heading"></p>
                <button type="button" class="gc-ef-close" aria-label="Close">
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </div>
            <div class="gc-ef-chips"></div>
            <textarea class="gc-ef-textarea" placeholder="Add a comment (optional)…" rows="2" maxlength="500"></textarea>
            <p class="gc-ef-error" role="alert" hidden></p>
            <div class="gc-ef-actions">
                <button type="button" class="gc-ef-regenerate-btn">
                    <i class="fa-solid fa-rotate-right"></i> Regenerate
                </button>
                <button type="button" class="gc-ef-submit-btn">
                    Submit <i class="fa-solid fa-check"></i>
                </button>
            </div>
            <div class="gc-ef-success">
                <span class="gc-ef-check"><i class="fa-solid fa-check"></i></span>
                Thanks for your feedback!
            </div>
        </div>
    `);

    _panel = document.getElementById(PANEL_ID);

    // Close button
    _panel.querySelector('.gc-ef-close').addEventListener('click', () => cancelPanel());

    // Submit button
    _panel.querySelector('.gc-ef-submit-btn').addEventListener('click', () => {
        doSubmit('submit');
    });

    // Regenerate button
    _panel.querySelector('.gc-ef-regenerate-btn').addEventListener('click', () => {
        doSubmit('regenerate');
    });

    // Ctrl/Cmd+Enter in textarea → submit
    _panel.querySelector('.gc-ef-textarea').addEventListener('keydown', e => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            doSubmit('submit');
        }
    });

    // Click outside → cancel
    document.addEventListener('click', e => {
        if (!_panel?.classList.contains('is-open')) return;
        if (_panel.contains(e.target)) return;
        // Don't close if clicking the like/dislike button itself
        if (e.target.closest?.('.g-chatform-message-actions-options-single')) return;
        cancelPanel();
    });

    // Esc → cancel
    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && _panel?.classList.contains('is-open')) cancelPanel();
    });
}

function positionPanel(anchor) {
    const rect = anchor.getBoundingClientRect();
    const panelW = 320;
    const margin = 10;
    const panelH = _panel.offsetHeight || 280; // estimated if not yet visible
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: center on anchor, clamped inside viewport
    let left = rect.left + rect.width / 2 - panelW / 2;
    left = Math.max(margin, Math.min(left, vw - panelW - margin));

    // Vertical: prefer below anchor; flip above if it would overflow the bottom
    const spaceBelow = vh - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    let top, arrowOnTop;

    if (spaceBelow >= panelH || spaceBelow >= spaceAbove) {
        // Place below anchor
        top = Math.min(rect.bottom + margin, vh - panelH - margin);
        arrowOnTop = true;
    } else {
        // Flip above anchor
        top = Math.max(margin, rect.top - panelH - margin);
        arrowOnTop = false;
    }

    // Final clamp so it never escapes viewport
    top = Math.max(margin, Math.min(top, vh - panelH - margin));

    _panel.style.left = left + 'px';
    _panel.style.top = top + 'px';

    // Position the arrow to point at the anchor button
    const arrowLeft = (rect.left + rect.width / 2) - left;
    const arrow = _panel.querySelector('.gc-ef-arrow');
    arrow.style.left = Math.max(20, Math.min(arrowLeft, panelW - 20)) + 'px';

    // Flip arrow to bottom of panel when panel is above anchor
    arrow.style.top = arrowOnTop ? '' : 'auto';
    arrow.style.bottom = arrowOnTop ? '' : '-7px';
    arrow.style.transform = arrowOnTop ? '' : 'rotate(180deg)';
}

function renderChips(type) {
    const chipsEl = _panel.querySelector('.gc-ef-chips');
    chipsEl.innerHTML = CONFIG[type].reasons.map(r =>
        `<button type="button" class="gc-ef-chip" data-reason="${r.label}">
            <span class="gc-ef-chip-icon">${r.icon}</span>
            <span>${r.label}</span>
         </button>`
    ).join('');

    // Chips toggle independently — multiple selections allowed
    chipsEl.querySelectorAll('.gc-ef-chip').forEach(chip => {
        chip.addEventListener('click', () => {
            chip.classList.toggle('is-active');
        });
    });
}

/** Build the combined feedback string from chips + textarea */
function collectFeedback() {
    const activeChips = [..._panel.querySelectorAll('.gc-ef-chip.is-active')]
        .map(c => c.dataset.reason);
    const comment = _panel.querySelector('.gc-ef-textarea').value.trim();
    const parts = [];
    if (activeChips.length) parts.push(activeChips.join(', '));
    if (comment) parts.push(comment);
    return parts.join(' — ') || null;
}

async function doSubmit(action) {
    if (_submitted) return;
    _submitted = true;
    _saving = true;

    const feedbackText = collectFeedback();
    const details = {
        topics: [..._panel.querySelectorAll('.gc-ef-chip.is-active')].map(c => c.dataset.reason),
        comment: _panel.querySelector('.gc-ef-textarea').value.trim(),
    };
    const buttons = [..._panel.querySelectorAll('.gc-ef-actions button, .gc-ef-close')];
    const error = _panel.querySelector('.gc-ef-error');
    error.hidden = true;
    buttons.forEach(button => { button.disabled = true; });
    try {
        if (action === 'regenerate') {
            await _callbacks?.onRegenerate?.(feedbackText, details);
        } else {
            await _callbacks?.onSubmit?.(feedbackText, details);
        }
    } catch (failure) {
        _submitted = false;
        error.textContent = failure.message || 'Feedback could not be saved. Please try again.';
        error.hidden = false;
        return;
    } finally {
        _saving = false;
        buttons.forEach(button => { button.disabled = false; });
    }

    _anchorEl?.classList.add('is-evaluated', `is-evaluated--${_type}`);
    _panel.querySelector('.gc-ef-success').classList.add('is-visible');
    _panel.querySelector('.gc-ef-actions').style.display = 'none';
    clearTimeout(_closeTimer);
    _closeTimer = setTimeout(closePanel, 1800);
}

function cancelPanel() {
    if (_saving) return;
    if (!_submitted) {
        _callbacks?.onCancel?.();
    }
    closePanel();
}

function closePanel() {
    _panel?.classList.remove('is-open');
    _callbacks = null;
    _type = null;
    _anchorEl = null;
    _submitted = false;
}

/**
 * Open the evaluation feedback panel.
 *
 * @param {'like'|'dislike'} type
 * @param {string}           messageId
 * @param {Element}          anchorEl    — .g-chatform-message-actions-options-single
 * @param {{ onSubmit, onRegenerate, onCancel }} callbacks
 */
export function showEvaluationFeedback(type, messageId, anchorEl, callbacks) {
    initPanel();

    clearTimeout(_closeTimer);
    _callbacks = callbacks || {};
    _type = type;
    _anchorEl = anchorEl;
    _submitted = false;

    const cfg = CONFIG[type];

    _panel.querySelector('.gc-ef-icon').className = `gc-ef-icon ${cfg.iconCls}`;
    _panel.querySelector('.gc-ef-icon').innerHTML = cfg.icon;
    _panel.querySelector('.gc-ef-heading').textContent = cfg.heading;
    _panel.querySelector('.gc-ef-textarea').value = '';
    _panel.querySelector('.gc-ef-error').hidden = true;
    _panel.querySelector('.gc-ef-success').classList.remove('is-visible');
    _panel.querySelector('.gc-ef-actions').style.display = '';

    // Show Regenerate button only on dislike
    _panel.querySelector('.gc-ef-regenerate-btn').style.display =
        type === 'dislike' ? '' : 'none';

    // Optionally hide Submit (e.g. when triggered from quick-action regenerate button)
    _panel.querySelector('.gc-ef-submit-btn').style.display =
        callbacks?.hideSubmit ? 'none' : '';

    renderChips(type);
    positionPanel(anchorEl);
    _panel.classList.add('is-open');

    // Re-position once visible so offsetHeight is accurate
    requestAnimationFrame(() => positionPanel(anchorEl));

    // Focus textarea for quick typing
    setTimeout(() => _panel.querySelector('.gc-ef-textarea').focus(), 120);
}
